/* ==========================================================================
   Custom Product Video Player
   Lightweight, modern player for product page videos.
   Initializes any [data-custom-video] block: handles autoplay (muted),
   custom play/pause/mute/fullscreen controls, progress bar, time display.
   ========================================================================== */
(function () {
  'use strict';

  function formatTime(seconds) {
    if (!isFinite(seconds) || seconds < 0) return '0:00';
    var m = Math.floor(seconds / 60);
    var s = Math.floor(seconds % 60);
    return m + ':' + (s < 10 ? '0' + s : s);
  }

  function init(wrap) {
    if (!wrap || wrap.dataset.customVideoInited === 'true') return;
    wrap.dataset.customVideoInited = 'true';

    var video = wrap.querySelector('[data-custom-video-el]');
    if (!video) return;

    var playBtns = wrap.querySelectorAll('[data-custom-video-play]');
    var muteBtn = wrap.querySelector('[data-custom-video-mute]');
    var fsBtn = wrap.querySelector('[data-custom-video-fullscreen]');
    var progress = wrap.querySelector('[data-custom-video-progress]');
    var progressBar = wrap.querySelector('[data-custom-video-progress-bar]');
    var timeEl = wrap.querySelector('[data-custom-video-time]');

    // Initial state: starts muted + paused, classes will toggle as play begins
    wrap.classList.add('is-paused');

    // Detect touch devices (controls always visible on touch)
    if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
      wrap.classList.add('is-touch');
    }

    function updatePlayState() {
      if (video.paused) {
        wrap.classList.remove('is-playing');
        wrap.classList.add('is-paused');
      } else {
        wrap.classList.remove('is-paused');
        wrap.classList.add('is-playing');
      }
    }

    function updateMuteState() {
      if (video.muted) {
        wrap.classList.remove('is-unmuted');
        if (muteBtn) muteBtn.setAttribute('aria-label', 'Unmute');
      } else {
        wrap.classList.add('is-unmuted');
        if (muteBtn) muteBtn.setAttribute('aria-label', 'Mute');
      }
    }

    function play() {
      var p = video.play();
      if (p && typeof p.catch === 'function') {
        p.catch(function () {
          // Autoplay blocked; ensure paused state shows so user can click
          wrap.classList.remove('is-playing');
          wrap.classList.add('is-paused');
        });
      }
    }

    function pause() {
      video.pause();
    }

    function togglePlay() {
      if (video.paused) play(); else pause();
    }

    function toggleMute() {
      video.muted = !video.muted;
      updateMuteState();
    }

    function toggleFullscreen() {
      if (document.fullscreenElement || document.webkitFullscreenElement) {
        if (document.exitFullscreen) document.exitFullscreen();
        else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
      } else {
        if (wrap.requestFullscreen) wrap.requestFullscreen();
        else if (wrap.webkitRequestFullscreen) wrap.webkitRequestFullscreen();
        else if (video.webkitEnterFullscreen) video.webkitEnterFullscreen();
      }
    }

    // Wire up buttons
    Array.prototype.forEach.call(playBtns, function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        togglePlay();
      });
    });

    if (muteBtn) {
      muteBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        toggleMute();
      });
    }

    if (fsBtn) {
      fsBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        toggleFullscreen();
      });
    }

    // Click on the video itself toggles play
    video.addEventListener('click', function () {
      togglePlay();
    });

    // Focus styling
    wrap.addEventListener('focusin', function () { wrap.classList.add('is-focused'); });
    wrap.addEventListener('focusout', function () { wrap.classList.remove('is-focused'); });

    // Progress bar interactions
    if (progress) {
      var seeking = false;

      function seekFromEvent(e) {
        var rect = progress.getBoundingClientRect();
        var clientX = (e.touches && e.touches[0]) ? e.touches[0].clientX : e.clientX;
        var ratio = (clientX - rect.left) / rect.width;
        ratio = Math.max(0, Math.min(1, ratio));
        if (isFinite(video.duration)) {
          video.currentTime = ratio * video.duration;
          updateProgress();
        }
      }

      progress.addEventListener('mousedown', function (e) {
        seeking = true;
        seekFromEvent(e);
      });
      document.addEventListener('mousemove', function (e) {
        if (seeking) seekFromEvent(e);
      });
      document.addEventListener('mouseup', function () { seeking = false; });

      progress.addEventListener('touchstart', function (e) {
        seeking = true;
        seekFromEvent(e);
      }, { passive: true });
      progress.addEventListener('touchmove', function (e) {
        if (seeking) seekFromEvent(e);
      }, { passive: true });
      document.addEventListener('touchend', function () { seeking = false; });
    }

    function updateProgress() {
      if (!isFinite(video.duration) || video.duration <= 0) return;
      var pct = (video.currentTime / video.duration) * 100;
      if (progressBar) progressBar.style.width = pct + '%';
      if (timeEl) timeEl.textContent = formatTime(video.currentTime);
    }

    video.addEventListener('timeupdate', updateProgress);
    video.addEventListener('loadedmetadata', function () {
      if (timeEl) timeEl.textContent = formatTime(0);
    });
    video.addEventListener('play', updatePlayState);
    video.addEventListener('playing', updatePlayState);
    video.addEventListener('pause', updatePlayState);
    video.addEventListener('volumechange', updateMuteState);

    // Listen for theme slideshow pause/play events (dispatched on parent .product__media)
    var slide = wrap.closest('[data-media-slide]');
    if (slide) {
      slide.addEventListener('pause', function () {
        pause();
      });
      slide.addEventListener('play', function () {
        play();
      });
      slide.addEventListener('play-desktop', function () {
        // Avoid autoplaying on touch devices via play-desktop signal
        if (!('ontouchstart' in window)) play();
      });
      slide.addEventListener('destroy', function () {
        pause();
      });
    }

    // Initial state sync
    updateMuteState();
    updatePlayState();

    // Try to autoplay (muted). Browsers allow muted autoplay.
    // Use IntersectionObserver to only play when in view.
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            play();
          } else {
            pause();
          }
        });
      }, { threshold: 0.25 });
      io.observe(wrap);
    } else {
      // Fallback: just play
      play();
    }
  }

  function initAll(root) {
    var scope = root || document;
    var wraps = scope.querySelectorAll('[data-custom-video]');
    Array.prototype.forEach.call(wraps, init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  // Re-init on Shopify section load (theme editor)
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });

  // Also try after a short delay in case the slideshow rendered later
  setTimeout(function () { initAll(); }, 500);
  setTimeout(function () { initAll(); }, 1500);
})();
